import { useEffect } from 'react';

import { getTopVisibleModal, isAvailableFocusTarget } from './overlay-focus.js';

const FOCUSABLE =
  'button, a[href], input:not([type="hidden"]), select, textarea, [tabindex], ' +
  '[contenteditable]:not([contenteditable="false"]), summary, audio[controls], video[controls]';

function tabStops(modal: HTMLElement) {
  const nodes = Array.from(modal.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (node) =>
      (node.tabIndex >= 0 ||
        (node.hasAttribute('contenteditable') && !node.hasAttribute('tabindex'))) &&
      isAvailableFocusTarget(node),
  );
  return nodes
    .filter((node) => {
      if (!(node instanceof HTMLInputElement) || node.type !== 'radio' || !node.name) return true;
      const group = nodes.filter(
        (other): other is HTMLInputElement =>
          other instanceof HTMLInputElement &&
          other.type === 'radio' &&
          other.name === node.name &&
          other.form === node.form,
      );
      return node === (group.find((radio) => radio.checked) ?? group[0]);
    })
    .sort((left, right) => {
      const order = (node: HTMLElement) => (node.tabIndex > 0 ? node.tabIndex : Infinity);
      return order(left) - order(right);
    });
}

function revealWithinModal(target: HTMLElement, modal: HTMLElement) {
  const select =
    target.getAttribute('role') === 'combobox' ? target.closest<HTMLElement>('.ant-select') : null;
  const frame = select && modal.contains(select) ? select : target;
  for (
    let container = frame.parentElement;
    container && modal.contains(container);
    container = container.parentElement
  ) {
    const style = window.getComputedStyle(container);
    const bounds = container.getBoundingClientRect();
    const targetBounds = frame.getBoundingClientRect();
    const top = bounds.top + container.clientTop;
    const left = bounds.left + container.clientLeft;
    if (
      container.clientHeight > 0 &&
      container.scrollHeight > container.clientHeight &&
      /auto|scroll/.test(style.overflowY || style.overflow)
    ) {
      const delta =
        targetBounds.top < top
          ? targetBounds.top - top
          : Math.max(0, targetBounds.bottom - top - container.clientHeight);
      if (delta) container.scrollTop += delta;
    }
    if (
      container.clientWidth > 0 &&
      container.scrollWidth > container.clientWidth &&
      /auto|scroll/.test(style.overflowX || style.overflow)
    ) {
      const delta =
        targetBounds.left < left
          ? targetBounds.left - left
          : Math.max(0, targetBounds.right - left - container.clientWidth);
      if (delta) container.scrollLeft += delta;
    }
  }
}

function containBoundaryTab(event: KeyboardEvent) {
  if (
    event.key !== 'Tab' ||
    event.defaultPrevented ||
    event.isComposing ||
    event.altKey ||
    event.ctrlKey ||
    event.metaKey
  ) {
    return;
  }
  const modal = getTopVisibleModal();
  const active = document.activeElement;
  if (!modal || !(event.target instanceof Node) || !modal.contains(event.target)) return;
  if (!(active instanceof HTMLElement) || !modal.contains(active)) return;
  const stops = tabStops(modal);
  const first = stops[0];
  const last = stops.at(-1);
  const atBoundary = event.shiftKey ? active === first : active === last;
  if (!first || atBoundary || active === modal) {
    event.preventDefault();
    const target = event.shiftKey ? (last ?? modal) : (first ?? modal);
    target.focus({ preventScroll: true });
    revealWithinModal(target, modal);
  }
}

// The library's focusin correction cannot run after native Tab leaves the document.
// Handle only the boundary; controls retain their own keys and in-dialog navigation.
export function OverlayKeyboardBoundary() {
  useEffect(() => {
    document.addEventListener('keydown', containBoundaryTab);
    return () => {
      document.removeEventListener('keydown', containBoundaryTab);
    };
  }, []);
  return null;
}
